import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getProductHandle, handleQuerySnapshot } from "../utilities";
import { DateTime } from "luxon";
import { useFirebase } from "../components/Firebase";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { IFirebaseBKSession } from "../interfaces/firebaseEntities";
import { TObjFromArray } from "../interfaces";
import axios, { AxiosResponse } from "axios";
import { useSession } from "../components/Session";
import { IAddedPlayer, IBattleKartConfig, IRemovedPlayer } from "../interfaces/battlekart";
  
interface IBattleKartContextType {
  loading: boolean;
  sessions: TObjFromArray<IFirebaseBKSession>;
  refreshData: () => void;
  startGame: (variantId: number, config: IBattleKartConfig[], clearedDrivers: IRemovedPlayer[], customPlayers: IAddedPlayer[]) => Promise<AxiosResponse>;
  updateSession: (sku: string, data: Partial<IFirebaseBKSession>) => Promise<AxiosResponse>;
}

const BattleKartContext = createContext<IBattleKartContextType>(undefined as unknown as IBattleKartContextType);

const BattleKartContextProviderComponent: FC<PropsWithChildren<{ value: IBattleKartContextType }>> = ({ value, children }) => {
  return (
    <BattleKartContext.Provider value={value}>{children}</BattleKartContext.Provider>
  );
};

export const useBattleKart = () => {
  return useContext<IBattleKartContextType>(BattleKartContext);
};

interface Type {
  date: string; // YYYY-MM-DD
}

const backendUrl = "https://us-central1-hyper-karting.cloudfunctions.net/bkRace";
// const backendUrl = "http://127.0.0.1:5001/hyper-karting/us-central1/bkRace";

const BattleKartContextProvider: FC<PropsWithChildren<Type>> = ({ children, date }) => {
  const handle = useMemo(()=> getProductHandle(DateTime.fromFormat(date, "yyyy-MM-dd")?.toJSDate(), "BATTLE_KART"), [date])

  const firebase = useFirebase();
  const db = useMemo(() => firebase.db, [firebase]);
  const bkSessionsRef = useMemo(() => collection(db, "bk_sessions"), [db]);
  const [sessions, setSessions] = useState<TObjFromArray<IFirebaseBKSession>>({});
  const [loading, setLoading] = useState(false);
  const { getToken } = useSession();

  const fetchData = useCallback(async (handle: string)=>{
    try {
      setLoading(true);
      const sessionSnapshot = await getDocs(query(bkSessionsRef, where("handle", "==", handle)));
      const sessionData = handleQuerySnapshot<IFirebaseBKSession>(sessionSnapshot)
      setSessions(sessionData);
    } catch (error) {
      console.log("error in fetching battle kart session data", error);
    }finally{
      setLoading(false);
    }
  },[bkSessionsRef])

  const refreshData = useCallback(async ()=>{
    if(handle){
      await fetchData(handle);
    }
  },[handle, fetchData])

  const startGame = useCallback(async (variantId: number, config: IBattleKartConfig[], clearedDrivers: IRemovedPlayer[], customPlayers: IAddedPlayer[])=>{
    const token = await getToken();
    const response = await axios.post(backendUrl,
        {
            variantId,
            config,
            clearedDrivers,
            customPlayers
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );
    return response;
  },[getToken])

  const updateSession = useCallback(async (sku: string, data: Partial<IFirebaseBKSession>)=>{
    const token = await getToken();
    const response = await axios.put(backendUrl,
        {
            sku,
            data
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    )
    return response;
  },[getToken])

  useEffect(() => {
    if(handle){
      fetchData(handle);
    }
  }, [handle, fetchData])

  useEffect(() => {
    //Listen realtime changes
    if (!handle) return;

    const q = query(bkSessionsRef, where('handle', '==', handle));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedBookings:TObjFromArray<IFirebaseBKSession> = {};
      snapshot.forEach((doc) => {
        const id = doc.id;
        const data = doc.data() as IFirebaseBKSession;
        if(data){
          updatedBookings[id] = data;
        }
      });
      console.log("updatedBookings", updatedBookings);
      setSessions((oldState) => ({ ...oldState, ...updatedBookings }));
    });


    return () => {
      unsubscribe && unsubscribe()
    };

  }, [bkSessionsRef, handle]);

  return (
    <BattleKartContextProviderComponent value={{ loading, sessions, refreshData, startGame, updateSession }}>
      {children}
    </BattleKartContextProviderComponent>
  );
};

export default BattleKartContextProvider;
  